import { useEffect, useState } from "react";
import { loadLeaderboardData } from "../utils/dataLoader";
import { LeaderboardTable } from "./leaderboard_table";
import { ModelPerformance } from "./model_performance";
import { LeaderboardPlot } from "./leaderboard_plot";
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import hpaiLogo from './hpai_logo_grad.png';
import bscLogo from './bsc-logo.png';

export function LeaderboardFilters({ theme, setTheme }) {
	const [activeTab, setActiveTab] = useState("leaderboard");
	const [metricsType, setMetricsType] = useState("s2r-cc");
	const [selectedTask, setSelectedTask] = useState("Spec-to-RTL");
	const [selectedBench, setSelectedBench] = useState("All");
	const [selectedSimulator, setSelectedSimulator] = useState("Icarus Verilog");
	const [selectedModelTypes, setSelectedModelTypes] = useState([]);
	const [includeDeprecated, setIncludeDeprecated] = useState(false);
	const [maxParams, setMaxParams] = useState(1500);
	const [searchQuery, setSearchQuery] = useState("");
	const [leaderboardData, setLeaderboardData] = useState([]);

	const tasks = ["Spec-to-RTL", "Code Completion", "Rule Completion", "Module Completion", "Line Completion"];
	const benchmarks = {
		"Spec-to-RTL": ["All", "VerilogEval S2R", "RTLLM"],
		"Code Completion": ["All", "VerilogEval MC", "VeriGen"],
		"Rule Completion": ["RuC"],
		"Module Completion": ["NotSoTiny"],
		"Line Completion": ["RTL-Repo"],
	};
	const simulators = {
		"Spec-to-RTL": ["Icarus Verilog", "Verilator"],
		"Code Completion": ["Icarus Verilog", "Verilator"],
		"Rule Completion": ["YoSys EQV"],
		"Module Completion": ["YoSys EQV"],
		"Line Completion": null, // no sims used
	}

	useEffect(() => {
		const taskBenchmarks = benchmarks[selectedTask];
		if (!taskBenchmarks.includes(selectedBench)) {
			setSelectedBench(benchmarks[selectedTask][0])
		}
	}, [selectedTask])

	useEffect(() => {
		const taskSimulators = simulators[selectedTask];
		if (taskSimulators === null) {
			setSelectedSimulator(null);
		} else if (!taskSimulators.includes(selectedSimulator)) {
			setSelectedSimulator(taskSimulators[0]);
		}
	}, [selectedTask]);

	useEffect(() => {
		async function loadData() {
			const data = await loadLeaderboardData(
				selectedTask,
				selectedBench,
				selectedSimulator,
				{
					modelTypes: selectedModelTypes,
					maxParams,
					includeDeprecated,
					searchQuery
				}
			);
			setLeaderboardData(data);
		}
		loadData();
	}, [selectedTask, selectedBench, selectedSimulator, selectedModelTypes, maxParams, includeDeprecated, searchQuery])

	return (
		<div className="w-full flex justify-center py-6 px-4 relative z-10">
			<div className="flex flex-col gap-4">
				{/* Tabs */}
				<div className="flex items-end justify-between gap-6 border-b border-gray-700 pr-2">
					<div className="flex gap-2">
						<button
							onClick={() => setActiveTab("leaderboard")}
							className={`px-4 py-1 text-sm font-medium transition-colors border-b-2 cursor-pointer ${
								activeTab === "leaderboard"
									? "border-green-500 text-green-400"
									: "border-transparent text-gray-400 hover:text-gray-300"
							}`}
						>
							Leaderboard
						</button>
						<button
							onClick={() => setActiveTab("performance")}
							className={`px-4 py-1 text-sm font-medium transition-colors border-b-2 cursor-pointer ${
								activeTab === "performance"
									? "border-green-500 text-green-400"
									: "border-transparent text-gray-400 hover:text-gray-300"
							}`}
						>
							Model Performance
						</button>
						<button
							onClick={() => setActiveTab("metrics")}
							className={`px-4 py-1 text-sm font-medium transition-colors border-b-2 cursor-pointer ${
								activeTab === "metrics"
									? "border-green-500 text-green-400"
									: "border-transparent text-gray-400 hover:text-gray-300"
							}`}
						>
							Metrics Info
						</button>
						<button
							onClick={() => setActiveTab("about")}
							className={`px-4 py-1 text-sm font-medium transition-colors border-b-2 cursor-pointer ${
								activeTab === "about"
									? "border-green-500 text-green-400"
									: "border-transparent text-gray-400 hover:text-gray-300"
							}`}
						>
							About
						</button>
					</div>
					<ThemeToggle theme={theme} setTheme={setTheme} />
				</div>

				{activeTab === "about" ? (
					<About />
				) : activeTab === "performance" ? (
					<ModelPerformance theme={theme} />
				) : activeTab === "leaderboard" ? (
					<>
						<div className="flex gap-4">
					<TaskFilter tasks={tasks} selectedTask={selectedTask} setSelectedTask={setSelectedTask} />
					<BenchmarkFilter benchmarks={benchmarks} selectedTask={selectedTask} selectedBench={selectedBench} setSelectedBench={setSelectedBench} />
					<SimulatorFilter simulators={simulators} selectedTask={selectedTask} selectedSimulator={selectedSimulator} setSelectedSimulator={setSelectedSimulator} />
				</div>
				<div className="w-full border-1 border-gray-700 rounded-lg bg-gray-900/20 shadow-lg backdrop-blur-sm pt-3 pb-3 px-4">
					<div className="flex justify-between items-end gap-4">
						<ModelSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
						<ModelTypeFilter selectedModelTypes={selectedModelTypes} setSelectedModelTypes={setSelectedModelTypes} />
						<DeprecatedCheckbox includeDeprecated={includeDeprecated} setIncludeDeprecated={setIncludeDeprecated} />
						<ParametersSlider maxParams={maxParams} setMaxParams={setMaxParams} />
					</div>
				</div>
				{selectedTask === "Module Completion" ?
					<div className="w-full border-1 border-gray-700 rounded-lg bg-gray-900/20 shadow-lg backdrop-blur-sm pt-3 pb-3 px-4">
						<MultiRangeSlider />
					</div>
					: null}
				<div className="mt-4 flex flex-col gap-8">
					<LeaderboardTable data={leaderboardData} task={selectedTask} />
					<div className="flex text-xs text-gray-500 text-left -mt-6 w-0 min-w-full">
						<span className="mr-1 flex-shrink-0">†</span>
						<span className="break-words overflow-hidden">All open-source models were evaluated using our TuRTLe framework powered by vLLM 0.8.5 V0 / 0.18.0 V1 with sampling parameters temperature=0.2 and top_p=0.95, pass@1 with N=5 at the MareNostrum 5 supercomputer. For closed-source models, we relied on OpenRouter with a fixed provider.</span>
					</div>
					<LeaderboardPlot data={leaderboardData} task={selectedTask} />
				</div>
					</>
				) : (
					<div className="w-full max-w-6xl min-w-6xl border-1 border-gray-700 rounded-lg bg-gray-900/20 shadow-lg backdrop-blur-sm overflow-hidden mt-4">
						{/* Metrics Type Toggle */}
						<div className="flex w-full border-b border-gray-700" style={{ backgroundColor: '#1A212B' }}>
							<button
								onClick={() => setMetricsType("s2r-cc")}
								className={`flex-1 px-6 py-2 text-sm font-medium transition-colors cursor-pointer border-r border-gray-700 ${
									metricsType === "s2r-cc"
										? "bg-gray-700/30 text-white"
										: "text-gray-400 hover:bg-gray-800/20"
								}`}
							>
								Spec-to-RTL & Code Completion
								<div className="text-xs text-gray-500 mt-1">VerilogEval, RTLLM, VeriGen</div>
							</button>
							<button
								onClick={() => setMetricsType("notsotiny")}
								className={`flex-1 px-6 py-2 text-sm font-medium transition-colors cursor-pointer border-r border-gray-700 ${
									metricsType === "notsotiny"
										? "bg-gray-700/30 text-white"
										: "text-gray-400 hover:bg-gray-800/20"
								}`}
							>
								Module Completion
								<div className="text-xs text-gray-500 mt-1">NotSoTiny</div>
							</button>
							<button
								onClick={() => setMetricsType("rtlrepo")}
								className={`flex-1 px-6 py-2 text-sm font-medium transition-colors cursor-pointer ${
									metricsType === "rtlrepo"
										? "bg-gray-700/30 text-white"
										: "text-gray-400 hover:bg-gray-800/20"
								}`}
							>
								Single Line Completion
								<div className="text-xs text-gray-500 mt-1">RTL-Repo</div>
							</button>
						</div>

						{/* Metrics Content */}
						<div className="p-6">
							{metricsType === "s2r-cc" ? (
								<S2RCCMetrics />
							) : metricsType === "notsotiny" ? (
								<NotSoTinyMetrics />
							) : (
								<RTLRepoMetrics />
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	)
};

function ThemeToggle({ theme, setTheme }) {
	return (
		<div className="flex items-center gap-2 pb-1">
			<button
				onClick={() => setTheme("dark")}
				className={`transition-colors cursor-pointer ${
					theme === "dark"
						? "text-white"
						: "text-gray-500 hover:text-gray-300"
				}`}
				aria-pressed={theme === "dark"}
				aria-label="Enable dark theme"
			>
				<MoonIcon />
			</button>
			<span className="text-xs text-gray-500 select-none">/</span>
			<button
				onClick={() => setTheme("light")}
				className={`transition-colors cursor-pointer ${
					theme === "light"
						? "text-amber-300"
						: "text-gray-500 hover:text-gray-300"
				}`}
				aria-pressed={theme === "light"}
				aria-label="Enable light theme"
			>
				<SunIcon />
			</button>
		</div>
	);
}

function SunIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.8}
			stroke="currentColor"
			className="h-5 w-5"
		>
			<circle cx="12" cy="12" r="4" />
			<path strokeLinecap="round" d="M12 2.5v2.25M12 19.25v2.25M21.5 12h-2.25M4.75 12H2.5M18.72 5.28l-1.59 1.59M6.87 17.13l-1.59 1.59M18.72 18.72l-1.59-1.59M6.87 6.87 5.28 5.28" />
		</svg>
	);
}

function MoonIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.8}
			stroke="currentColor"
			className="h-5 w-5"
		>
			<path strokeLinecap="round" strokeLinejoin="round" d="M21 12.8A8.8 8.8 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
		</svg>
	);
}

export function ModelSearch({ searchQuery, setSearchQuery }) {
	return (
		<div className="flex flex-col gap-2 backdrop-blur-sm">
			<p className="text-xs font-semibold text-gray-400 capitalize tracking-wide text-left">
				Select Model
			</p>
			<div>
				<form className="max-w-sm" onSubmit={(e) => e.preventDefault()}>
					<label htmlFor="search" className="block mb-2 text-xs font-medium text-gray-800 sr-only">Type model name...</label>
					<div className="relative">
						<div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
							<svg className="w-4 h-4 text-body" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" /></svg>
						</div>
						<input
							type="search"
							id="search"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="block w-full px-3 py-2 ps-9 bg-neutral-secondary-medium border border-gray-600 rounded-xl text-heading text-sm rounded-base focus:ring-brand focus:border-brand shadow-xs placeholder:text-gray-400/40"
							placeholder="Type model name..."
						/>
					</div>
				</form>
			</div>
		</div>
	)
}

export function DeprecatedCheckbox({ includeDeprecated, setIncludeDeprecated }) {
	return (
		<div className="flex flex-col gap-2 backdrop-blur-sm">
			<p className="text-xs font-semibold text-gray-400 capitalize tracking-wide text-left">
				Deprecated Models
			</p>
			<label className="px-3 py-2 rounded-lg border cursor-pointer transition-all duration-200 flex items-center gap-2 border-gray-700 bg-transparent text-gray-300 hover:border-gray-600 hover:bg-gray-800/30" >
				<input
					type="checkbox"
					checked={includeDeprecated}
					onChange={(e) => setIncludeDeprecated(e.target.checked)}
					className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-brand focus:ring-brand focus:ring-offset-gray-900 cursor-pointer"
				/>
				<span className="text-sm whitespace-nowrap">Include deprecated</span>
			</label>
		</div>
	);
}

export function ParametersSlider({ maxParams, setMaxParams }) {
	return (
		<div className="flex flex-col backdrop-blur-sm min-w-[220px]">
			<div className="flex justify-between items-center mb-2">
				<p className="text-xs font-semibold text-gray-400 capitalize tracking-wide text-left relative pb-2">
					Parameters
				</p>
				<p className="text-xs font-semibold text-gray-300">
					{maxParams === 1500 ? "All" : `< ${maxParams}B`}
				</p>
			</div>
			<input
				type="range"
				min="0"
				max="1500"
				step="25"
				value={maxParams}
				onChange={(e) => setMaxParams(Number(e.target.value))}
				className="w-full h-4 bg-gray-500 rounded-lg appearance-none cursor-pointer accent-brand mb-3"
				style={{
					background: `linear-gradient(to right, rgb(95, 100, 107) 0%, rgb(95, 100, 107) ${(maxParams / 1500) * 100}%, rgb(55, 65, 81) ${(maxParams / 1500) * 100}%, rgb(55, 65, 81) 100%)`
				}}
			/>
		</div>
	);
}

export function ModelTypeFilter({ selectedModelTypes, setSelectedModelTypes }) {
	const modelTypes = [
		{ name: "General", color: "bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20 text-yellow-200" },
		{ name: "Coding", color: "bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20 text-blue-200" },
		{ name: "RTL-Specific", color: "bg-green-500/10 border-green-500/30 hover:bg-green-500/20 text-green-200" },
		{ name: "Reasoning", color: "bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 text-purple-200" }
	];

	const showColors = selectedModelTypes.length > 0;

	const toggleModelType = (name) => {
		if (selectedModelTypes.includes(name)) {
			setSelectedModelTypes(selectedModelTypes.filter(type => type !== name));
		} else {
			setSelectedModelTypes([name]);
		}
	};

	return (
		<div className="flex flex-col gap-2 backdrop-blur-sm">
			<p className="text-xs font-semibold text-gray-400 capitalize tracking-wide text-left">
				Select Model Type
			</p>
			<div className="flex flex-row gap-2 flex-wrap">
				{modelTypes.map(({ name, color }) => {
					const isSelected = selectedModelTypes.includes(name);
					return (
						<label
							key={name}
							className={`
								px-3 py-1.5 rounded-lg border cursor-pointer transition-all duration-200
								${isSelected
									? showColors
										? `${color} border-opacity-60`
										: 'border-gray-500 bg-gray-700/50 text-white shadow-lg'
									: 'border-gray-700 bg-transparent text-gray-300 hover:border-gray-600 hover:bg-gray-800/30'
								}
							`}
						>
							<input
								type="checkbox"
								name="modelType"
								value={name}
								checked={isSelected}
								onChange={() => toggleModelType(name)}
								className="sr-only"
							/>
							<span className="text-sm whitespace-nowrap">{name}</span>
						</label>
					);
				})}
			</div>
		</div>
	);
}

export function TaskFilter({ tasks, selectedTask, setSelectedTask }) {
	return (
		<FilterComponent
			filterName="task"
			elements={tasks}
			selectedElement={selectedTask}
			setSelectedElement={setSelectedTask} />
	)
}

export function BenchmarkFilter({ benchmarks, selectedTask, selectedBench, setSelectedBench }) {
	const taskBenches = benchmarks[selectedTask];

	return (
		<div className="min-w-[300px]">
			<FilterComponent
				filterName="benchmark"
				elements={taskBenches}
				selectedElement={selectedBench}
				setSelectedElement={setSelectedBench} />
		</div>
	)
}

export function SimulatorFilter({ simulators, selectedTask, selectedSimulator, setSelectedSimulator }) {
	var available_sims = simulators[selectedTask];
	const disabled = available_sims === null

	if (available_sims === null) {
		// we do this to show something in the case of rtlrepo
		var available_sims = ["Icarus Verilog", "Verilator"];
	}


	return (
		<div className="min-w-[250px]">
			<FilterComponent
				filterName="simulator"
				elements={available_sims || []}
				selectedElement={selectedSimulator}
				setSelectedElement={setSelectedSimulator}
				disabled={disabled} />
		</div>
	)
}

export function FilterComponent({ filterName, elements, selectedElement, setSelectedElement, disabled = false }) {
	return (
		<div className="flex flex-col gap-2 p-3 border-1 border-gray-700 rounded-lg bg-gray-900/20 shadow-lg backdrop-blur-sm">
			<p className="text-xs font-semibold text-gray-400 capitalize tracking-wide text-left">
				Select {filterName}
			</p>

			<div className="flex flex-row gap-1.5">
				{
					elements.map(element => {
						const isSelected = selectedElement === element;
						return (
							<label
								key={element}
								className={`
									px-3 py-1.5 rounded-lg border-2 cursor-pointer transition-all duration-200
									${disabled
										? 'border-gray-700 bg-gray-800/30 text-gray-600 cursor-not-allowed'
										: isSelected
											? 'border-gray-500 bg-gray-700/50 text-white shadow-lg'
											: 'border-gray-700 bg-transparent text-gray-300 hover:border-gray-600 hover:bg-gray-800/30'
									}
								`}
							>
								<input
									type="radio"
									name={filterName}
									value={element}
									checked={isSelected}
									onChange={() => { if (!disabled) setSelectedElement(element) }}
									disabled={disabled}
									className="sr-only"
								/>
								<span className="text-sm whitespace-nowrap">{element}</span>
							</label>
						);
					})
				}
			</div>
		</div>
	)
}

export function MultiRangeSlider({ }) {
	const timelineMonths = ["Dec 2025", "Jan 2026", "Feb 2026", "Mar 2026", "Apr 2026", "May 2026", "Jun 2026", "Jul 2026"];

	return (
		<div className="w-full flex flex-col items-center gap-3">
			<div className="w-full flex justify-between relative">
				<div className="absolute top-[24.5px] left-7 right-7 h-[1px] bg-gray-600"></div>
				{timelineMonths.map((month) => (
					<div key={month} className="flex flex-col items-center z-10">
						<span className="text-xs text-gray-400">{month}</span>
						<span className="text-xs text-gray-400">|</span>
					</div>
				))}
			</div>
		</div>
	);
}

function About() {
	return (
		<div className="w-full max-w-6xl min-w-6xl border-1 border-gray-700 rounded-lg bg-gray-900/20 shadow-lg backdrop-blur-sm p-8 mt-4">
			<div className="text-gray-300 space-y-6 text-left">
				{/* Logos */}
				<div className="flex items-center justify-center gap-8 mb-8">
					<div className="bg-white p-6 rounded-lg">
						<img src={hpaiLogo} alt="HPAI Group Logo" className="h-32" />
					</div>
					<div className="bg-white p-6 rounded-lg">
						<img src={bscLogo} alt="BSC Logo" className="h-32" />
					</div>
				</div>

				{/* Description */}
				<div>
					<p className="text-sm leading-relaxed mb-6">
						The High-Performance Artificial Intelligence (HPAI) group is part of the Barcelona Supercomputing Center (BSC). This leaderboard is maintained by HPAI as part of our commitment to open science.
					</p>

					{/* Links */}
					<ul className="list-disc list-inside text-sm space-y-2 mb-6 ml-4">
						<li>
							<a
								href="https://hpai.bsc.es"
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-400 hover:text-blue-300 underline"
							>
								HPAI Website
							</a>
						</li>
						<li>
							<a
								href="https://github.com/HPAI-BSC"
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-400 hover:text-blue-300 underline"
							>
								HPAI GitHub Organization Page
							</a>
						</li>
						<li>
							<a
								href="https://huggingface.co/HPAI-BSC"
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-400 hover:text-blue-300 underline"
							>
								HPAI Hugging Face Organization Page
							</a>
						</li>
					</ul>

					{/* Contact */}
					<div>
						<p className="text-sm text-gray-400 mb-2">Feel free to contact us:</p>
						<p className="text-sm">
							Email: <a href="mailto:hpai@bsc.es" className="text-blue-400 hover:text-blue-300 underline">hpai@bsc.es</a>
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}

function S2RCCMetrics() {
	return (
		<div className="text-gray-300 space-y-6 text-left">
			<p className="text-sm leading-relaxed">
				Given a benchmark with <InlineMath>n</InlineMath> problems, the TuRTLe framework generates <InlineMath>m=5</InlineMath> candidate solutions for each. Then, each generation is processed sequentially through the evaluation pipeline: Syntax Correctness (STX), Functional Correctness (FNC), Synthesizability (SYN), and Post-Synthesis Quality (PSQ). Each stage only processes results that passed the previous one, and <strong>failures in previous stages are reported as automatic fails in the next ones</strong>, creating a cascade score where STX <InlineMath>\ge</InlineMath> FNC <InlineMath>\ge</InlineMath> SYN. It could happen that SYN <InlineMath>&lt;</InlineMath> PSQ because we contemplate the possibility of having PSQ better than the human reference (even though it is highly unlikely).
			</p>

			<div>
				<h3 className="text-lg font-semibold text-gray-200 mb-3">Tools for the Evaluation</h3>
				<p className="text-sm leading-relaxed">
					STX is evaluated by compiling a design along with its testbench using <strong>Icarus Verilog</strong> and checking for errors. If no errors are issued at compile time, FNC is evaluated by running the simulation executable generated by the compiler and checking if the testbench passes. Functionally correct codes are tested for SYN, by elaborating the design with <strong>OpenLANE (Yosys)</strong>.
				</p>
				<p className="text-sm leading-relaxed mt-3">
					For those designs that pass SYN, PSQ is measured with the PPA report, which is computed by using <strong>OpenLANE</strong> to synthesize the code into a netlist. Designs are synthesized using the SKY130A open-source PDK with a <InlineMath>10ns</InlineMath> delay constraint. We extract area and power directly from the PPA report and evaluate performance based on maximum delay, defined as the difference between the clock period and the worst slack reported by static timing analysis. In this way, all three PPA metrics are represented as positive numbers, where 0 represents the minimum possible value, leaving the maximum unbounded.
				</p>
			</div>

			<div>
				<h3 className="text-lg font-semibold text-gray-200 mb-3">Compute Metrics</h3>
				<p className="text-sm leading-relaxed">
					Since STX, FNC and SYN are binary evaluations, pass or fail, their score is computed using Pass@1. Remember that failures in previous stages are reported as automatic fails in the next ones.
				</p>
				<p className="text-sm leading-relaxed mt-3">
					On the other hand, PPA is represented as real values that must be evaluated against a golden solution (crafted by humans in our case). Therefore, we need to introduce a new formulation that takes this into account.
				</p>
			</div>

			<div>
				<h3 className="text-lg font-semibold text-gray-200 mb-3">PPA-Score</h3>
				<p className="text-sm leading-relaxed mb-3">
					Let <InlineMath>{String.raw`p_{i,j}`}</InlineMath> represent the PPA metric (power, performance, or area) from the LLM for candidate <InlineMath>j</InlineMath> of problem <InlineMath>i</InlineMath>. We compute a score for each generation (represented as <InlineMath>{String.raw`\hat{p}_{i,j}`}</InlineMath>) computed according to the following steps:
				</p>
				<ul className="list-disc list-inside text-sm space-y-2 ml-4">
					<li>Each <InlineMath>{String.raw`p_{i,j}`}</InlineMath> is compared against the corresponding PPA value <InlineMath>g_i</InlineMath> of the golden solution. For that, instead of aggregating <InlineMath>{String.raw`p_{i,j}`}</InlineMath> we compute <InlineMath>{String.raw`p_{i,j}/g_i \in (0,+\infty)`}</InlineMath>.</li>
					<li>For generations that do not pass STX, FNC and SYN evaluations, <InlineMath>{String.raw`p_{i,j}`}</InlineMath> cannot be computed. As a result, we set a failure value of <InlineMath>{String.raw`2 \cdot g_i`}</InlineMath> (e.g. producing a design two times bigger than the human reference in the case of the area metric). This approach also requires us to clip results that pass the previous evaluations but perform worse than this threshold.</li>
					<li>We flip the result so that the metric behaves as the rest of the goals STX, FNC, and SYN (higher is better).</li>
				</ul>
				<p className="text-sm leading-relaxed mt-3">
					The resulting score <InlineMath>{String.raw`\hat{p}_{i,j}`}</InlineMath> of generation <InlineMath>j</InlineMath> for problem <InlineMath>i</InlineMath> is defined as
				</p>
				<div className="bg-gray-800/50 p-4 rounded-lg mt-4 text-left">
					<BlockMath>{String.raw`\hat{p}_{i,j} = \begin{cases} 2 - \min(p_{i,j}/g_i, 2) & \text{if } p_{i,j} \text{ exists} \\ 0 & \text{otherwise} \end{cases}`}</BlockMath>
				</div>
				<p className="text-sm leading-relaxed mt-3">which has the following interpretation:</p>
				<ul className="list-disc list-inside text-sm space-y-2 ml-4 mt-2">
					<li><InlineMath>{String.raw`\hat{p}_{i,j} = 0`}</InlineMath>: means that the generation has not passed the previous stages, or that it requires twice or more the area, the power, or the delay (performance), when compared to the human reference.</li>
					<li><InlineMath>{String.raw`\hat{p}_{i,j} = 1`}</InlineMath>: are designs with an area, power, or performance equal to that of the human reference.</li>
					<li><InlineMath>{String.raw`\hat{p}_{i,j} = 2`}</InlineMath>: can only be obtained by chips which occupy no space, execute in no time, or consume no energy (perfect, but impossible to achieve).</li>
				</ul>
				<p className="text-sm leading-relaxed mt-3">
					The final formula considering all generations of an LLM for a given benchmark is just the average of these scores:
				</p>
				<div className="bg-gray-800/50 p-4 rounded-lg mt-2 text-left">
					<BlockMath>{String.raw`x\text{-score} = \left[\frac{1}{n \cdot m} \sum_{i=1}^n \sum_{j=1}^m \hat{p}_{i,j}\right] \cdot 100`}</BlockMath>
				</div>
				<p className="text-sm leading-relaxed mt-3">
					where <InlineMath>{String.raw`x \in \{\text{Power, Performance, Area}\}`}</InlineMath>. Finally, we define the PPA-score as the average of these three metrics.
				</p>
				<p className="text-sm leading-relaxed mt-3">
					Note that under the assumption that all generations are synthetizable and produce the same PPA as the golden solution (i.e., the human baseline), the PPA-score would be 100%. The theoretical upper bound is 200, but it is impossible to achieve in practice.
				</p>
			</div>

			<div>
				<h3 className="text-lg font-semibold text-gray-200 mb-3">Aggregated Scores</h3>
				<p className="text-sm leading-relaxed">
					In the case of MC and S2R tasks, we report all numbers from the four evaluation stages (STX, FNC, SYN, and PSQ). However, to determine the best model for these tasks, it is necessary to aggregate the results.
				</p>
			</div>

			<div>
				<h4 className="text-base font-semibold text-gray-200 mb-2">For a single benchmark</h4>
				<p className="text-sm leading-relaxed">
					Due to the cascade effect previously explained, the final stage of the evaluation (i.e., the PPA-Score) compresses the information from all previous stages. Therefore, we interpret it as the model's final score for the benchmark.
				</p>
			</div>

			<div>
				<h4 className="text-base font-semibold text-gray-200 mb-2">Across Multiple Benchmarks of the same task</h4>
				<p className="text-sm leading-relaxed">
					When aggregating results across benchmarks for the same task, we cannot simply take a straightforward average because some benchmarks are much larger than others. Instead, we weight each benchmark's contribution by its size, ensuring that each individual sample contributes equally to the overall result.
				</p>
			</div>
		</div>
	);
}

function NotSoTinyMetrics() {
	return (
		<div className="text-gray-300 space-y-6 text-left">
			<p className="text-sm leading-relaxed">
				For our new benchmark NotSoTiny, based on Tiny Tapeout shuttles, we found out a lack in Functional robustness of evaluations using test-benches. Where only a small minority of the Tiny Tapeout candidate projects testbenches, approximately 25%, contain more than one assertion and achieve at least 80% code coverage and the median number of assertions is a mere 1 per project. It is because of this that we rely on a "testbench-free" approach using Equivalence Checking as our primary strategy for functional correctness. Specifically, we employ equivalence checking to verify the equivalence of each generated module against the golden reference implementation.
			</p>

			<div>
				<h3 className="text-lg font-semibold text-gray-200 mb-3">Formal Verification (EQV)</h3>
				<p className="text-sm leading-relaxed mb-3">
					The verification flow is based on the Yosys suite, and it proceeds in three phases:
				</p>
				<ol className="list-decimal list-inside text-sm space-y-2 ml-4">
					<li>The golden RTL implementation and the LLM-generated candidate are both loaded into the tool</li>
					<li>The tool constructs a miter circuit: a composite structure that feeds identical inputs to both implementations, compares all corresponding outputs bit-wise through XNOR gates, and reduces these comparisons to a single equivalence signal. If the two designs are functionally identical, this signal remains high for all possible input combinations; any discrepancy drives it low, yielding a concrete counterexample.</li>
					<li>We apply <em>formal verification</em> to the miter. We rely on two passes offered by Yosys: <code className="bg-gray-800/50 px-1 py-0.5 rounded">equiv_simple</code> and <code className="bg-gray-800/50 px-1 py-0.5 rounded">equiv_induct</code>.</li>
				</ol>
				<p className="text-sm leading-relaxed mt-3">
					<code className="bg-gray-800/50 px-1 py-0.5 rounded">equiv_simple</code> performs combinational equivalence checking as a SAT instance over the miter circuit. If the SAT solver returns UNSAT, no such distinguishing input exists, and the designs are proven to be combinatorially equivalent across all possible inputs.
				</p>
				<p className="text-sm leading-relaxed mt-3">
					<code className="bg-gray-800/50 px-1 py-0.5 rounded">equiv_induct</code> extends verification to sequential logic using bounded k-induction.
				</p>
			</div>

			<div>
				<h3 className="text-lg font-semibold text-gray-200 mb-3">Partition (Cell) Coverage</h3>
				<p className="text-sm leading-relaxed">
					While formal equivalence checking provides a binary pass/fail result, we introduce partition coverage as a fine-grained metric for partial credit. For the sake of equivalence checking, Yosys represents each design as a set of logic partitions. For the benchmark involving multiple partitions, we define <em>Partition Coverage</em> as follows:
				</p>
				<div className="bg-gray-800/50 p-4 rounded-lg mt-4 text-left">
					<BlockMath>{String.raw`\text{Partition Coverage} = \frac{\sum_{n \in \text{equivalent partitions}} \text{partitions}(n)}{\sum_{m \in \text{all partitions}} \text{partitions}(m)} \times 100\%`}</BlockMath>
				</div>
				<p className="text-sm leading-relaxed mt-3">
					This metric quantifies how much of the golden design's functionality the LLM successfully reproduced.
				</p>
			</div>
		</div>
	);
}

function RTLRepoMetrics() {
	return (
		<div className="text-gray-300 space-y-6 text-left">
			<p className="text-sm leading-relaxed">
				RTL-Repo evaluates models on Single Line Completion (SLC) tasks using Exact Matching (EM) as the primary metric.
			</p>
			<div>
				<h3 className="text-lg font-semibold text-gray-200 mb-3">Exact Matching (EM)</h3>
				<p className="text-sm leading-relaxed">
					For Single Line Completion tasks, we use Exact Matching to evaluate the model's predictions. A prediction is considered correct if and only if it exactly matches the ground truth line, character for character.
				</p>
				<p className="text-sm leading-relaxed mt-3">
					This strict metric ensures that the generated code is syntactically and semantically identical to the reference solution, providing a clear and unambiguous measure of the model's performance on line-level code completion tasks.
				</p>
			</div>
		</div>
	);
}
